import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { SessionSliceState } from './types';
import {
  GetCurrentUserResponse,
  Organization,
  UserRequestBody,
} from 'src/services/apiInterfaces';
import * as APIv1 from 'src/services/apiV1';
import { updateUserBrandFilter } from 'src/services/brand/brandFilterThunks';
import { mergeBrandFilterResponse } from 'src/services/brand/brandFilterUtils';
import { fetchUsersMe } from 'src/services/zendeskUser';
import type { RootState } from 'src/store/rootReducer';

async function getCurrentUserFromUsersMe(): Promise<GetCurrentUserResponse> {
  if (IS_ZENDESK_BUILD) {
    const [me, currentUserResponse] = await Promise.all([
      fetchUsersMe(),
      APIv1.getCurrentUser(),
    ]);
    // Zendesk-provisioned orgs use `full_name` as the source of truth for
    // the user's display name.
    // first_name/last_name are intentionally left blank
    return {
      current_user: {
        brand_filters: currentUserResponse.current_user.brand_filters,
        email: me.user.email,
        enabled: true,
        first_name: '',
        full_name: (me.user.name ?? '').trim(),
        last_name: '',
        permission_level: me.ft_role,
        user_id: String(me.user.id),
        user_notification_preferences: {
          solve_widget_error: { email: false },
        },
      },
      organization: {
        display_name: null,
        email_domains: [],
        org_helpdesk: '',
        org_type: 'customer_production',
        org_type_display_name: 'Production',
      },
    };
  }

  return APIv1.getCurrentUser();
}

const initialState: SessionSliceState = {
  currentUser: {
    data: null,
    error: false,
    loading: false,
  },
  organization: {
    data: null,
    error: false,
    loading: false,
  },
};

export const getCurrentUser = createAsyncThunk(
  'session/getCurrentUser',
  getCurrentUserFromUsersMe,
);
export const updateUser = createAsyncThunk(
  'session/updateUser',
  ({
    userId,
    userRequestBody,
  }: {
    userId: string;
    userRequestBody: UserRequestBody;
  }) => APIv1.updateUser(userId, userRequestBody),
);

export const updateOrganization = createAsyncThunk(
  'session/updateOrganization',
  ({ organization }: { organization: Partial<Organization> }) =>
    APIv1.updateOrganization(organization),
);

const sessionSlice = createSlice({
  extraReducers: builder => {
    /**
     * getCurrentUser
     */
    builder.addCase(getCurrentUser.fulfilled, (state, action) => {
      const { current_user, organization } = action.payload;
      state.currentUser.data = current_user;
      state.organization.data = organization;
      state.currentUser.loading = false;
      state.organization.loading = false;
    });
    builder.addCase(getCurrentUser.pending, state => {
      state.currentUser.error = false;
      state.organization.error = false;
      state.currentUser.loading = true;
      state.organization.loading = true;
    });
    builder.addCase(getCurrentUser.rejected, state => {
      state.currentUser.error = true;
      state.organization.error = true;
      state.currentUser.loading = false;
      state.organization.loading = false;
    });
    /**
     * updateUser
     */
    builder.addCase(updateUser.fulfilled, (state, action) => {
      const previousBrandFilters = state.currentUser.data?.brand_filters;
      state.currentUser.data = {
        ...action.payload,
        brand_filters: action.payload.brand_filters ?? previousBrandFilters,
      };
      state.currentUser.loading = false;
    });
    builder.addCase(updateUser.pending, state => {
      state.currentUser.error = false;
      state.currentUser.loading = true;
    });
    builder.addCase(updateUser.rejected, state => {
      state.currentUser.error = true;
      state.currentUser.loading = false;
    });
    builder.addCase(updateUserBrandFilter.fulfilled, (state, action) => {
      if (
        !state.currentUser.data ||
        action.meta.arg.userId !== state.currentUser.data.user_id
      ) {
        return;
      }
      const { brandFilterRequest } = action.meta.arg;
      state.currentUser.data = {
        ...state.currentUser.data,
        brand_filters: mergeBrandFilterResponse(
          state.currentUser.data.brand_filters,
          brandFilterRequest.product,
          action.payload,
        ),
      };
    });
    /**
     * udpateOrganization
     */
    builder.addCase(updateOrganization.fulfilled, (state, action) => {
      state.organization.data = action.payload.organization;
      state.organization.loading = false;
    });
    builder.addCase(updateOrganization.pending, state => {
      state.organization.error = false;
      state.organization.loading = true;
    });
    builder.addCase(updateOrganization.rejected, state => {
      state.organization.error = true;
      state.organization.loading = false;
    });
  },
  initialState,
  name: 'session',
  reducers: {},
});

export const selectSession = (state: RootState): SessionSliceState =>
  state.session;

export default sessionSlice.reducer;
